import { setCookieData, convertPhoneNumberFormat } from '@repo/common-utils';
import { useCommonRouter } from '@repo/platform';
import { useUserEventLogger } from '@repo/tracking-service';
import { BoxButton, Divider, Fieldset, TextField, TextButton, useDialog, IconButton } from '@repo/ui';
import { useAuthContext, removeReturnUrl } from '@repo/user-session';
import Link from 'next/link';
import { ChangeEvent, KeyboardEvent, useState } from 'react';

import { BZNAV_LAST_SIGN_IN_COOKIE_KEY, SOCIAL_ITEMS } from '../constants';
import { useFormValidation } from '../hooks/use-form-validation';
import { FormFieldMap, FormFieldProps, SignItemType, SocialItemType } from '../types';
import { checkEmailValidation, checkPasswordValidation } from '../utils';

export const EmailSignInForm = () => {
  const allFields: ('email' | 'password')[] = ['email', 'password'];
  const fieldForm: Record<'email' | 'password', FormFieldMap> = {
    email: {
      placeholder: '이메일 주소',
      type: 'email',
      autoComplete: 'email',
      validation: checkEmailValidation
    },
    password: {
      placeholder: '비밀번호',
      type: 'password',
      autoComplete: 'new-password',
      validation: checkPasswordValidation
    }
  };
  const router = useCommonRouter();
  const { sendClickEvent } = useUserEventLogger();
  const { showDialog } = useDialog();
  const { authService, setStatusSignChecking } = useAuthContext();
  const { fields } = useFormValidation(['email', 'password']);
  const isAllFieldsValid = allFields.every((field) => fields[field]?.inputState.value && fieldForm[field].validation(fields[field]?.inputState.value).isValid);

  const emailSignIn = async () => {
    setCookieData(BZNAV_LAST_SIGN_IN_COOKIE_KEY, 'email', { maxAge: 60 * 60 * 24 * 30 });
    let isValid = true;
    allFields.forEach((field) => {
      const isFieldValid = fields[field]?.validate(fieldForm[field].validation);
      if (!isFieldValid) isValid = false;
    });

    if (!isValid) return;

    const returnUrl = await authService?.signService.startEmailSignIn(fields.email?.inputState.value ?? '', fields.password?.inputState.value ?? '');
    removeReturnUrl();
    if (returnUrl) {
      sendClickEvent('sign-in_cta', { type: 'email' }, { isUseAirbridge: true });
      setStatusSignChecking();
      router.replace(returnUrl);
    } else {
      showDialog({
        title: '로그인을 실패했어요',
        description: '입력하신 이메일 주소와 비밀번호가 일치하지 않아요.\n다시 확인해주세요.',
        buttons: {
          primary: {
            onClick: () => {
              fields.email?.clear();
              fields.password?.clear();
            }
          }
        }
      });
    }
  };

  return (
    <div className={'w-full pb-normal-large'}>
      <form>
        {allFields.map((field) => {
          if (fields[field]) {
            const filedFormItem = fieldForm[field];
            return (
              <FormField
                key={field}
                inputState={fields[field].inputState}
                onValueChange={fields[field].setValue}
                checkValidation={() => fields[field]?.validate(filedFormItem.validation) ?? false}
                onKeyupEnter={emailSignIn}
                placeholder={filedFormItem.placeholder}
                type={filedFormItem.type}
                autoComplete={filedFormItem.autoComplete}
              />
            );
          }
          return null;
        })}
      </form>
      <BoxButton variant={'filled-primary'} size={'medium'} disabled={!isAllFieldsValid} className={'mt-tiny-large mb-2.5 w-full'} onClick={emailSignIn}>
        로그인
      </BoxButton>
      {authService && (
        <div className={'pt-normal-small'}>
          <Link className="text-gray-700" href={authService.signPaths.forgotPassword}>
            <BoxButton size={'xsmall'} variant={'tonal-secondary'}>
              비밀번호를 잊으셨나요?
            </BoxButton>
          </Link>
        </div>
      )}
    </div>
  );
};

export const SocialRectangleButton = ({ item, lastSignInItem }: { item: SocialItemType; lastSignInItem?: SignItemType }) => {
  const { authService } = useAuthContext();
  const { sendClickEvent } = useUserEventLogger();

  const startSocialSignIn = () => {
    sendClickEvent('sign-in_cta', { type: item }, { isUseAirbridge: true });
    setCookieData(BZNAV_LAST_SIGN_IN_COOKIE_KEY, item, { maxAge: 60 * 60 * 24 * 30 });
    authService?.signService.startSocialSignIn(item);
  };

  return (
    <BoxButton className={`flex w-full ${SOCIAL_ITEMS[item].buttonColor}`} onClick={startSocialSignIn}>
      <img width={SOCIAL_ITEMS[item].rectangleButtonIconWidth} alt="social-button" src={SOCIAL_ITEMS[item].imageUrl} />
      <span className={`ml-[7.5px] font-medium ${SOCIAL_ITEMS[item].textColor}`}>
        {lastSignInItem === item ? SOCIAL_ITEMS[item].retryText : SOCIAL_ITEMS[item].text}
      </span>
    </BoxButton>
  );
};

export const SocialCircleButton = ({ item }: { item: SocialItemType }) => {
  const { authService } = useAuthContext();
  const { sendClickEvent } = useUserEventLogger();

  const startSocialSignIn = () => {
    sendClickEvent('sign-in_cta', { type: item }, { isUseAirbridge: true });
    setCookieData(BZNAV_LAST_SIGN_IN_COOKIE_KEY, item, { maxAge: 60 * 60 * 24 * 30 });
    authService?.signService.startSocialSignIn(item);
  };

  return (
    <button className={`flex h-[64px] w-[64px] items-center justify-center rounded-[100px] ${SOCIAL_ITEMS[item].buttonColor}`} onClick={startSocialSignIn}>
      <img width={SOCIAL_ITEMS[item].circleButtonIconWidth} alt={SOCIAL_ITEMS[item].text} src={SOCIAL_ITEMS[item].imageUrl} />
    </button>
  );
};

export const EmailTextButtonSection = ({ isShowLoginButton, loginButtonAction }: { isShowLoginButton: boolean; loginButtonAction: () => void }) => {
  const { authService } = useAuthContext();

  return (
    <div className="flex w-full items-center justify-center mt-[24px]">
      {isShowLoginButton && (
        <>
          <TextButton
            variant={'secondary'}
            size={'small'}
            onClick={() => {
              loginButtonAction();
            }}
          >
            이메일로 로그인
          </TextButton>
          <Divider className={'mx-normal-small !w-[1px] !h-3.5'} />
        </>
      )}
      {authService && (
        <Link href={authService.signPaths.signUpInput}>
          <TextButton variant={'secondary'} size={'small'}>
            이메일로 회원가입{!isShowLoginButton ? '$>;' : ''}
          </TextButton>
        </Link>
      )}
    </div>
  );
};

export const FormField = ({ label, inputState, onValueChange, onKeyupEnter, checkValidation, placeholder, type = 'text', autoComplete }: FormFieldProps) => {
  const isPasswordType = type === 'password';
  const inputType = isPasswordType ? 'text' : type;
  const [isMasked, setIsMasked] = useState(isPasswordType);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = type === 'tel' ? convertPhoneNumberFormat(event.target.value) : event.target.value;
    onValueChange(value);
  };

  const handleKeyUp = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && onKeyupEnter) {
      onKeyupEnter();
    }
  };

  const commonProps = {
    value: inputState.value,
    onChange: handleChange,
    onKeyUp: handleKeyUp,
    onBlur: checkValidation,
    placeholder,
    autoComplete: autoComplete || 'off'
  };

  return (
    <Fieldset isInvalid={!!inputState.errorMessage} errorMessage={inputState.errorMessage} label={label}>
      <TextField
        type={inputType}
        error={!!inputState.errorMessage}
        className={'relative h-12'}
        masked={isMasked}
        {...commonProps}
        trailingElement={
          isPasswordType ? (
            <IconButton
              type="button"
              iconType={isMasked ? 'eye-slash' : 'eye'}
              size={'small'}
              className={'absolute top-[5px] right-[9px]'}
              onClick={() => setIsMasked(!isMasked)}
            />
          ) : undefined
        }
      />
    </Fieldset>
  );
};
